import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHospitalOnboarding } from '../../../contexts/HospitalOnboardingContext';
import {
  Edit3,
  Truck,
  Building2,
  Users,
  MapPin,
  Tag,
  Activity,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  FileCheck,
  AlertCircle,
  Mic,
  Loader2,
  Check,
  Navigation,
  FileText
} from 'lucide-react';
import { getAmbulanceTypeAbbreviation } from '../../../utils/ambulanceTypeHelper';

export const Step6FleetReviewSubmit = () => {
  const navigate = useNavigate();
  const {
    formData,
    setCurrentStep,
    updateFields,
    goBack,
    submitApplication,
    saveAndExit,
    isSaving
  } = useHospitalOnboarding();

  const [confirmed, setConfirmed] = useState(formData.confirmed_accurate || false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleConfirmationChange = (e) => {
    const val = e.target.checked;
    setConfirmed(val);
    updateFields({ confirmed_accurate: val });
    if (val) setErrorMsg('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!confirmed) {
      setErrorMsg('Please review and check the compliance confirmation box before submitting.');
      return;
    }

    const result = await submitApplication();
    if (result) {
      if (typeof window !== 'undefined' && window.__ccOnboardingSubmitted) {
        window.__ccOnboardingSubmitted(result);
        return;
      }
      navigate('/hospital/onboarding/submitted?type=fleet');
    }
  };

  const ambulances = Array.isArray(formData.ambulances) && formData.ambulances.length > 0
    ? formData.ambulances
    : [];

  const rateCards = Array.isArray(formData.rate_cards) && formData.rate_cards.length > 0
    ? formData.rate_cards
    : [];

  const fleetCapacities = Array.isArray(formData.fleet_capacities)
    ? formData.fleet_capacities.filter((c) => c.ambulance_type || c.owned || c.attached || c.available_daily)
    : [];

  const operatingAreas = Array.isArray(formData.operating_areas)
    ? formData.operating_areas.filter((a) => a.area || a.pincode || a.area_pin)
    : [];

  // Fallbacks for base location & coverage
  const primaryBaseLocation =
    formData.primary_base_location ||
    ambulances[0]?.pickup_stands?.[0]?.stand_name ||
    ambulances[0]?.pickup_stands?.[0]?.map_location ||
    ambulances[0]?.parking_location ||
    operatingAreas[0]?.area ||
    operatingAreas[0]?.area_pin ||
    (formData.city && formData.state ? `${formData.city}, ${formData.state}` : formData.city || formData.state || '—');

  const primaryPincode =
    formData.primary_pincode ||
    ambulances[0]?.pickup_stands?.[0]?.pincode ||
    operatingAreas[0]?.pincode ||
    formData.pincode ||
    '—';

  const serviceRadius =
    formData.service_radius_km ||
    ambulances[0]?.pickup_stands?.[0]?.service_radius_km ||
    ambulances[0]?.service_radius ||
    '';

  const operatingHours = formData.operating_hours || '24x7';

  const whatsappDisplay =
    formData.whatsapp_number ||
    (formData.is_whatsapp_same ? (formData.admin_phone || formData.mobile || formData.contact_info) : '') ||
    '—';

  const topClients = Array.isArray(formData.top_clients)
    ? formData.top_clients.filter((c) => c.client || c.rides_month || c.type)
    : [];

  const corridors = Array.isArray(formData.long_distance_corridors)
    ? formData.long_distance_corridors.filter((c) => c.pickup || c.destination)
    : [];

  return (
    <div className="w-full">
      {/* Header Row: Step info and Switch to Voice */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6 relative pb-4 border-b border-slate-200">
        <div>
          <span className="text-xs font-semibold uppercase tracking-wider text-blue-600 block mb-1">
            STEP 6 OF 6
          </span>
          <h2 className="text-2xl sm:text-3xl font-semibold text-slate-800 tracking-tight leading-tight">
            Review & submit
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Review your fleet operator profile, operational capacity, rates, and vehicles before final submission.
          </p>
          {/* Smart Ambulance Blue Accent Line */}
          <div className="w-14 sm:w-16 h-0.5 bg-blue-600 mt-2 rounded-full" />
        </div>

        <button
          type="button"
          onClick={() => navigate('/hospital/onboarding/voice?type=fleet&step=6')}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-slate-200 hover:border-slate-300 text-xs font-medium text-slate-700 shadow-2xs transition-all cursor-pointer self-start sm:self-auto"
        >
          <Mic className="w-3.5 h-3.5 text-slate-600" />
          <span>Switch to voice</span>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* CARD 1: Fleet Owner Profile Summary */}
        <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-200/90 shadow-sm p-6 sm:p-8">
          <div className="flex items-center justify-between pb-4 mb-5 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600">
                <Truck className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-800">Fleet owner profile</h3>
                <p className="text-[11px] text-slate-400">Operator credentials, identification and registration</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setCurrentStep(1)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-xs font-semibold text-blue-600 cursor-pointer transition-colors"
            >
              <Edit3 className="w-3.5 h-3.5" />
              <span>Edit</span>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 text-xs">
            <div>
              <span className="text-slate-400 block text-[11px] font-medium">Fleet Operator Name</span>
              <span className="font-semibold text-slate-800 text-sm">
                {formData.operator_name || '—'}
              </span>
            </div>

            <div>
              <span className="text-slate-400 block text-[11px] font-medium">Operator Category</span>
              <span className="font-semibold text-slate-800 text-sm">
                {formData.category || 'Standard Fleet'}
              </span>
            </div>

            <div>
              <span className="text-slate-400 block text-[11px] font-medium">City & State</span>
              <span className="font-semibold text-slate-800 text-sm">
                {formData.city || formData.state ? `${formData.city || ''}${formData.city && formData.state ? ', ' : ''}${formData.state || ''}` : '—'}
              </span>
            </div>

            <div>
              <span className="text-slate-400 block text-[11px] font-medium">Primary Contact / Phone</span>
              <span className="font-semibold text-slate-800 text-sm font-mono">
                {formData.admin_phone || formData.mobile || formData.contact_info || '—'}
              </span>
            </div>

            <div>
              <span className="text-slate-400 block text-[11px] font-medium">WhatsApp Number</span>
              <span className="font-semibold text-slate-800 text-sm font-mono">
                {whatsappDisplay}
              </span>
            </div>

            <div>
              <span className="text-slate-400 block text-[11px] font-medium">Official Email</span>
              <span className="font-semibold text-slate-800 text-sm">
                {formData.admin_email || formData.email || '—'}
              </span>
            </div>

            <div>
              <span className="text-slate-400 block text-[11px] font-medium">Admin / Operations Lead</span>
              <span className="font-semibold text-slate-800 text-sm">
                {formData.admin_name || '—'}
              </span>
            </div>

            <div>
              <span className="text-slate-400 block text-[11px] font-medium">Business / Trade License</span>
              <span className="font-semibold text-slate-800 text-sm font-mono">
                {formData.business_license || '—'}
              </span>
            </div>

            <div>
              <span className="text-slate-400 block text-[11px] font-medium">GSTIN</span>
              <span className="font-semibold text-slate-800 text-sm font-mono">
                {formData.gstin || '—'}
              </span>
            </div>

            <div>
              <span className="text-slate-400 block text-[11px] font-medium">PAN</span>
              <span className="font-semibold text-slate-800 text-sm font-mono">
                {formData.pan || '—'}
              </span>
            </div>

            <div>
              <span className="text-slate-400 block text-[11px] font-medium">Years in Ambulance Business</span>
              <span className="font-semibold text-slate-800 text-sm">
                {formData.years_in_business ? `${formData.years_in_business} Years` : (formData.years_in_ambulance_business ? `${formData.years_in_ambulance_business} Years` : '—')}
              </span>
            </div>

            <div className="sm:col-span-2 lg:col-span-2">
              <span className="text-slate-400 block text-[11px] font-medium">Registered Address</span>
              <span className="font-semibold text-slate-800 text-sm">
                {formData.registered_address || formData.address || '—'}
              </span>
            </div>
          </div>

          {Array.isArray(formData.fleet_specialization) && formData.fleet_specialization.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <span className="text-[11px] font-semibold text-slate-500 block mb-2">Fleet Specializations:</span>
              <div className="flex flex-wrap gap-1.5">
                {formData.fleet_specialization.map((spec) => (
                  <span key={spec} className="px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 text-xs font-medium border border-blue-100">
                    {spec}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* CARD 2: Capacity & Coverage Summary */}
        <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-200/90 shadow-sm p-6 sm:p-8">
          <div className="flex items-center justify-between pb-4 mb-5 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600">
                <MapPin className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-800">Capacity & coverage</h3>
                <p className="text-[11px] text-slate-400">Total fleet capability and primary operational jurisdiction</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setCurrentStep(2)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-xs font-semibold text-blue-600 cursor-pointer transition-colors"
            >
              <Edit3 className="w-3.5 h-3.5" />
              <span>Edit</span>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 text-xs">
            <div>
              <span className="text-slate-400 block text-[11px] font-medium">Primary Base / Dispatch Hub</span>
              <span className="font-semibold text-slate-800 text-sm line-clamp-1" title={primaryBaseLocation}>
                {primaryBaseLocation}
              </span>
            </div>

            <div>
              <span className="text-slate-400 block text-[11px] font-medium">Primary PIN Code</span>
              <span className="font-semibold text-slate-800 text-sm font-mono">
                {primaryPincode}
              </span>
            </div>

            <div>
              <span className="text-slate-400 block text-[11px] font-medium">Operating Hours</span>
              <span className="font-semibold text-slate-800 text-sm">
                {operatingHours}
              </span>
            </div>

            <div>
              <span className="text-slate-400 block text-[11px] font-medium">Service Radius</span>
              <span className="font-semibold text-slate-800 text-sm">
                {serviceRadius ? `${serviceRadius} KM` : (ambulances[0]?.pickup_stands?.[0]?.service_radius_km ? `${ambulances[0].pickup_stands[0].service_radius_km} KM` : '—')}
              </span>
            </div>
          </div>

          {/* Fleet Capacities Breakdown Table */}
          {fleetCapacities.length > 0 && (
            <div className="mt-5 pt-5 border-t border-slate-100">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-600">
                  Fleet Capacity by Configuration ({fleetCapacities.length} Types)
                </span>
                <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-md border border-blue-100">
                  Total Fleet: {fleetCapacities.reduce((acc, r) => acc + (Number(r.owned) || 0) + (Number(r.attached) || 0), 0)} ambulances
                </span>
              </div>
              <div className="overflow-x-auto border border-slate-200/80 rounded-xl">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200/80">
                    <tr>
                      <th className="py-2.5 px-3 w-10 text-center">#</th>
                      <th className="py-2.5 px-3 min-w-[140px]">Ambulance Type</th>
                      <th className="py-2.5 px-3 text-center min-w-[90px]">Owned</th>
                      {fleetCapacities.some((r) => r.attached) && (
                        <th className="py-2.5 px-3 text-center min-w-[90px]">Attached</th>
                      )}
                      <th className="py-2.5 px-3 text-center min-w-[110px]">Available Daily</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {fleetCapacities.map((row, idx) => (
                      <tr key={row.id || idx} className="hover:bg-slate-50/50">
                        <td className="py-2.5 px-3 text-center font-medium text-slate-400">{idx + 1}</td>
                        <td className="py-2.5 px-3 font-semibold text-slate-800" title={row.ambulance_type}>
                          <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-[11px] font-semibold border border-blue-200/60">
                            {getAmbulanceTypeAbbreviation(row.ambulance_type) || row.ambulance_type || '—'}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-center font-medium text-slate-700">
                          {row.owned || '0'}
                        </td>
                        {fleetCapacities.some((r) => r.attached) && (
                          <td className="py-2.5 px-3 text-center font-medium text-slate-700">
                            {row.attached || '0'}
                          </td>
                        )}
                        <td className="py-2.5 px-3 text-center font-bold text-emerald-600">
                          {row.available_daily || '0'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Operating Areas List if filled */}
          {operatingAreas.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-600 block mb-2">
                Operating Areas & Zones:
              </span>
              <div className="flex flex-wrap gap-2">
                {operatingAreas.map((area, idx) => (
                  <span key={idx} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-slate-100 text-slate-700 text-xs font-medium border border-slate-200">
                    <MapPin className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                    <span>{area.area || area.area_pin}</span>
                    {area.pincode && <span className="text-slate-400 font-mono text-[11px]">({area.pincode})</span>}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* CARD 3: Rides & Clients Summary */}
        <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-200/90 shadow-sm p-6 sm:p-8">
          <div className="flex items-center justify-between pb-4 mb-5 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600">
                <Activity className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-800">Rides & client patterns</h3>
                <p className="text-[11px] text-slate-400">Monthly trip volume, revenue metrics and partner institutions</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setCurrentStep(3)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-xs font-semibold text-blue-600 cursor-pointer transition-colors"
            >
              <Edit3 className="w-3.5 h-3.5" />
              <span>Edit</span>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 text-xs">
            <div>
              <span className="text-slate-400 block text-[11px] font-medium">Monthly Completed Rides</span>
              <span className="font-semibold text-slate-800 text-sm">
                {formData.monthly_rides?.completed_rides ? `${formData.monthly_rides.completed_rides} rides/month` : '—'}
              </span>
            </div>

            <div>
              <span className="text-slate-400 block text-[11px] font-medium">Average Trip Fare</span>
              <span className="font-semibold text-slate-800 text-sm">
                {formData.monthly_rides?.average_fare ? `₹${formData.monthly_rides.average_fare}` : '—'}
              </span>
            </div>

            <div>
              <span className="text-slate-400 block text-[11px] font-medium">Average Trip Distance</span>
              <span className="font-semibold text-slate-800 text-sm">
                {formData.monthly_rides?.average_trip_km ? `${formData.monthly_rides.average_trip_km} KM` : '—'}
              </span>
            </div>

            <div>
              <span className="text-slate-400 block text-[11px] font-medium">Top Hospital / Client</span>
              <span className="font-semibold text-slate-800 text-sm">
                {topClients[0]?.client || formData.top_clients?.[0]?.client || '—'}
              </span>
            </div>
          </div>

          {/* Top Clients Table */}
          {topClients.length > 0 && (
            <div className="mt-5 pt-5 border-t border-slate-100">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-600 block mb-2.5">
                Top Client Partners ({topClients.length})
              </span>
              <div className="overflow-x-auto border border-slate-200/80 rounded-xl">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200/80">
                    <tr>
                      <th className="py-2.5 px-3">Client / Hospital</th>
                      <th className="py-2.5 px-3">Type</th>
                      <th className="py-2.5 px-3 text-center">Rides / Month</th>
                      <th className="py-2.5 px-3 text-center">Avg Fare</th>
                      <th className="py-2.5 px-3">Payment Terms</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {topClients.map((client, idx) => (
                      <tr key={client.id || idx} className="hover:bg-slate-50/50">
                        <td className="py-2 px-3 font-semibold text-slate-800">{client.client || '—'}</td>
                        <td className="py-2 px-3 text-slate-600">{client.type || 'Hospital'}</td>
                        <td className="py-2 px-3 text-center font-medium text-slate-700">{client.rides_month || '—'}</td>
                        <td className="py-2 px-3 text-center font-mono text-blue-600">{client.average_fare ? `₹${client.average_fare}` : '—'}</td>
                        <td className="py-2 px-3 text-slate-500 text-[11px]">{client.payment || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Long Distance Corridors if any */}
          {corridors.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-600 block mb-2">
                Long Distance Corridors
              </span>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                {corridors.map((c, idx) => (
                  <div key={idx} className="p-2.5 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-between">
                    <div>
                      <span className="font-semibold text-slate-800 block">{c.pickup || '—'} → {c.destination || '—'}</span>
                      <span className="text-slate-400 text-[11px]">{c.trips_month || '0'} trips/month</span>
                    </div>
                    <span className="font-bold text-blue-600 text-xs">
                      {c.approx_fare ? `₹${c.approx_fare}` : '—'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* CARD 4: Rate Card Tariff */}
        <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-200/90 shadow-sm p-6 sm:p-8">
          <div className="flex items-center justify-between pb-4 mb-5 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600">
                <Tag className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-800">Rate card tariff</h3>
                <p className="text-[11px] text-slate-400">Configured vehicle rates and distance pricing structure</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setCurrentStep(4)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-xs font-semibold text-blue-600 cursor-pointer transition-colors"
            >
              <Edit3 className="w-3.5 h-3.5" />
              <span>Edit</span>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {rateCards.length === 0 ? (
              <p className="text-xs text-slate-400 italic py-2 col-span-3">No rate cards added yet.</p>
            ) : (
              rateCards.map((rc, idx) => (
                <div
                  key={idx}
                  className="bg-slate-50/90 rounded-2xl p-4 border border-slate-200/90 hover:border-blue-200 transition-colors text-xs"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span
                      className="px-2.5 py-0.5 rounded-md bg-blue-100/70 text-blue-800 font-bold text-xs"
                      title={rc.ambulance_type}
                    >
                      {getAmbulanceTypeAbbreviation(rc.ambulance_type) || rc.ambulance_type || 'Custom Type'}
                    </span>
                    <span className="font-mono font-bold text-blue-600 text-sm">
                      ₹{rc.per_km || '0'}<span className="text-[11px] font-normal text-slate-500">/km</span>
                    </span>
                  </div>

                  <div className="space-y-1 text-slate-600 text-[11px] pt-1 border-t border-slate-200/60">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Base Fare:</span>
                      <span className="font-semibold text-slate-800">
                        ₹{rc.base_rate || '0'} {rc.base_km ? `(incl. ${rc.base_km} KM)` : ''}
                      </span>
                    </div>
                    {rc.waiting_charges_hr && (
                      <div className="flex justify-between">
                        <span className="text-slate-400">Waiting:</span>
                        <span className="font-medium text-slate-700">₹{rc.waiting_charges_hr}/hr</span>
                      </div>
                    )}
                    {rc.night_charges_percent && (
                      <div className="flex justify-between">
                        <span className="text-slate-400">Night Surge:</span>
                        <span className="font-medium text-slate-700">{rc.night_charges_percent}%</span>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* CARD 5: Enrolled Ambulance Inventory */}
        <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-200/90 shadow-sm p-6 sm:p-8">
          <div className="flex items-center justify-between pb-4 mb-5 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600">
                <FileCheck className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-800">
                  Enrolled ambulances ({ambulances.length})
                </h3>
                <p className="text-[11px] text-slate-400">Full vehicle profile, pickup stand coordinates and clinical capabilities</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setCurrentStep(5)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-xs font-semibold text-blue-600 cursor-pointer transition-colors"
            >
              <Edit3 className="w-3.5 h-3.5" />
              <span>Edit</span>
            </button>
          </div>

          <div className="space-y-4">
            {ambulances.length === 0 ? (
              <p className="text-xs text-slate-400 italic py-2">No ambulances added yet.</p>
            ) : (
              ambulances.map((amb, idx) => {
                const titleText =
                  amb.registration_number ||
                  amb.vehicle_code ||
                  (amb.make_model ? `${amb.make_model} (#${idx + 1})` : `Ambulance #${idx + 1}`);

                const stands = Array.isArray(amb.pickup_stands) ? amb.pickup_stands : [];
                const equipment = Array.isArray(amb.medical_equipment) ? amb.medical_equipment : [];
                const tech = Array.isArray(amb.technology) ? amb.technology : [];
                const docs = Object.entries(amb.documents || {}).filter(([_, v]) => Boolean(v));

                return (
                  <div
                    key={amb.id || idx}
                    className="bg-slate-50/90 rounded-2xl p-5 border border-slate-200 hover:border-blue-200 transition-all text-xs space-y-4"
                  >
                    {/* Header Row: Number, Registration/Code, Type, Status */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-200/70">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center font-bold text-xs shrink-0 shadow-xs">
                          {idx + 1}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono font-bold text-slate-900 text-sm">
                              {titleText}
                            </span>
                            {amb.vehicle_code && amb.registration_number && (
                              <span className="text-[11px] font-medium text-slate-500 bg-white px-2 py-0.5 rounded border border-slate-200">
                                Code: {amb.vehicle_code}
                              </span>
                            )}
                            {amb.ambulance_type && (
                              <span
                                className="px-2.5 py-0.5 rounded-md bg-blue-50 text-blue-700 text-[11px] font-bold border border-blue-200/80"
                                title={amb.ambulance_type}
                              >
                                {getAmbulanceTypeAbbreviation(amb.ambulance_type) || amb.ambulance_type}
                              </span>
                            )}
                          </div>
                          <span className="text-slate-500 text-[11px] mt-0.5 block">
                            {[
                              amb.make_model,
                              amb.manufacture_year ? `Year: ${amb.manufacture_year}` : '',
                              amb.ownership ? `Ownership: ${amb.ownership}` : ''
                            ]
                              .filter(Boolean)
                              .join(' • ') || 'Standard Emergency Ambulance'}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 self-start sm:self-auto">
                        <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1">
                          <Check className="w-3 h-3 text-emerald-600" />
                          <span>{amb.operating_status || 'Active & On-Duty'}</span>
                        </span>
                      </div>
                    </div>

                    {/* Pickup Stands (Selected on Google Maps) */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
                          <MapPin className="w-3.5 h-3.5 text-blue-600" />
                          <span>Pickup Stands & Locations ({stands.length})</span>
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                        {stands.map((stand, sIdx) => (
                          <div
                            key={stand.id || sIdx}
                            className="bg-white p-3 rounded-xl border border-slate-200/90 shadow-2xs space-y-1.5"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <span className="font-semibold text-slate-800 text-xs flex items-center gap-1">
                                <span className="w-4 h-4 rounded-full bg-slate-100 text-slate-600 text-[10px] inline-flex items-center justify-center font-bold">
                                  {sIdx + 1}
                                </span>
                                <span>{stand.stand_name || 'Primary Stand'}</span>
                              </span>
                              {stand.service_radius_km && (
                                <span className="text-[10px] font-medium text-slate-500 bg-slate-50 px-2 py-0.5 rounded border border-slate-200 shrink-0">
                                  {stand.service_radius_km} KM Radius
                                </span>
                              )}
                            </div>

                            <p className="text-[11px] text-slate-600 leading-snug flex items-start gap-1">
                              <Navigation className="w-3 h-3 text-blue-500 mt-0.5 shrink-0" />
                              <span className="line-clamp-2">
                                {stand.map_location || [stand.area, stand.pincode].filter(Boolean).join(', ') || 'Map location not set'}
                              </span>
                            </p>

                            <div className="flex items-center gap-3 text-[10px] text-slate-400 pt-1 border-t border-slate-100">
                              <span>Area: <strong className="text-slate-600 font-medium">{stand.area || '—'}</strong></span>
                              <span>PIN: <strong className="text-slate-600 font-medium font-mono">{stand.pincode || '—'}</strong></span>
                              {stand.intercity_available && (
                                <span>Intercity: <strong className="text-slate-600 font-medium">{stand.intercity_available}</strong></span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Medical Equipment & Clinical Capabilities */}
                    {equipment.length > 0 && (
                      <div>
                        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600 block mb-1.5">
                          Medical Equipment & Clinical Capabilities ({equipment.length})
                        </span>
                        <div className="flex flex-wrap gap-1.5">
                          {equipment.map((eq) => (
                            <span
                              key={eq}
                              className="px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 text-[11px] font-semibold border border-blue-200/70"
                            >
                              {eq}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Technology & Telematics */}
                    {tech.length > 0 && (
                      <div>
                        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600 block mb-1.5">
                          Technology & Telematics ({tech.length})
                        </span>
                        <div className="flex flex-wrap gap-1.5">
                          {tech.map((tItem) => (
                            <span
                              key={tItem}
                              className="px-2.5 py-1 rounded-lg bg-slate-100 text-slate-700 text-[11px] font-medium border border-slate-200"
                            >
                              {tItem}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Documents Uploaded */}
                    {docs.length > 0 && (
                      <div className="pt-2 border-t border-slate-200/70 flex items-center gap-2 flex-wrap">
                        <span className="text-[11px] font-semibold text-slate-500">Documents Attached:</span>
                        {docs.map(([k]) => (
                          <span
                            key={k}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[10px] font-semibold border border-emerald-200"
                          >
                            <Check className="w-3 h-3 text-emerald-600" />
                            <span>{k.replace(/_/g, ' ').toUpperCase()}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Simple Attestation Checkbox Line */}
        <div className="pt-2 px-1">
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={handleConfirmationChange}
              className="mt-0.5 w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-slate-300 cursor-pointer shrink-0"
            />
            <span className="text-xs sm:text-sm text-slate-700 leading-normal">
              <strong className="font-semibold text-slate-800">Verification & Compliance:</strong>{' '}
              I confirm that all vehicle registration documents, driver credentials, medical equipment inventories, and service tariffs provided above are accurate and compliant with local ambulance transport regulations.
            </span>
          </label>

          {errorMsg && (
            <p className="text-xs text-red-600 font-medium mt-2 pl-7">
              {errorMsg}
            </p>
          )}
        </div>

        {/* Navigation Action Buttons with Blue Theme */}
        <div className="flex items-center justify-between pt-6 border-t border-slate-200">
          <button
            type="button"
            onClick={goBack}
            className="px-5 py-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs sm:text-sm font-medium transition-all cursor-pointer"
          >
            Back
          </button>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={saveAndExit}
              disabled={isSaving}
              className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 text-xs sm:text-sm font-medium transition-all cursor-pointer"
            >
              Save & exit
            </button>

            <button
              type="submit"
              disabled={isSaving}
              className="px-7 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-medium text-xs sm:text-sm shadow-md hover:shadow-lg transition-all flex items-center gap-2 cursor-pointer disabled:opacity-75 disabled:cursor-wait"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-white" />
                  <span>{formData.status === 'SUBMITTED' ? 'Saving Updates...' : 'Submitting Fleet Application...'}</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  <span>{formData.status === 'SUBMITTED' ? 'Save & Update Application' : 'Submit Fleet Application'}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Step6FleetReviewSubmit;
